import React from 'react';
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ActivityIndicator,
  // ViewStyle,
  // TextStyle,
} from 'react-native';
import Entypo from '@react-native-vector-icons/entypo/static';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { fs, iconSize, s, v } from '../../utils/libs';
import { GTFonts, theme } from '../../theme';

type FontAwesome6SolidIconName = Extract<
  React.ComponentProps<typeof FontAwesome6>,
  { iconStyle: 'solid' }
>['name'];

interface ButtonProps {
  disabled?: boolean;
  fontSize?: number;
  onPress: () => void;
  text: string;
  rippleColor?: string;
  backgroundColor: string;
  height?: number;
  icon?:
    | FontAwesome6SolidIconName
    | 'lock'
    | 'chevron-right'
    | 'plus-circle'
    | '';
  iconSize?: number;
  /** Icon after the label, shown alongside a leading `icon` (plus-circle + chevron). */
  trailingIcon?: 'chevron-right' | 'arrow-right';
  opacity?: number;
  width?: number;
  paddingHorizontal?: number;
  borderRadius: number;
  fontFamily?: string;
  loading?: boolean;
  textColor?: string;
  /** When true, button stretches to fill parent width (ignores `width` prop) */
  fullWidth?: boolean;
  /** Outline colour. Defaults to the fill colour, i.e. no visible outline. */
  borderColor?: string;
  /** Outline width. Unset keeps the button borderless, as before. */
  borderWidth?: number;
}

const Button: React.FC<ButtonProps> = ({
  disabled,
  fontSize,
  onPress,
  text,
  rippleColor,
  backgroundColor,
  height,
  icon,
  iconSize: iconSizeProp,
  trailingIcon,
  opacity,
  width,
  paddingHorizontal,
  borderRadius,
  fontFamily,
  loading,
  textColor,
  fullWidth,
  borderColor,
  borderWidth,
}) => {
  const touchDisabled = !!disabled || !!loading;
  return (
    <View style={[styles.container, fullWidth && styles.fullWidth]}>
      <TouchableOpacity
        disabled={touchDisabled}
        // android_ripple={{ color: rippleColor, borderless: false, foreground: true }}
        hitSlop={{
          top: v(10),
          bottom: v(10),
          left: s(10),
          right: s(10),
        }}
        onPress={onPress}
        style={[
          styles.button,
          {
            borderRadius: borderRadius,
          },
          {
            backgroundColor: backgroundColor,
            // Outlined buttons sit flat; filled ones keep their slight lift.
            elevation: touchDisabled || borderWidth ? 0 : 1.5,
            height: height,
            width: fullWidth ? undefined : width,
            paddingHorizontal: paddingHorizontal,
            borderColor: borderColor ?? backgroundColor,
            borderWidth: borderWidth,
            opacity: opacity,
          },
          disabled && { backgroundColor: theme.colors.borderGray },
        ]}>
        {icon === 'plus-circle' ? (
          <MaterialCommunityIcons
            name="plus-circle"
            style={{ marginRight: s(8) }}
            size={iconSize(iconSizeProp ?? 20)}
            color={textColor || 'white'}
          />
        ) : null}
        {icon &&
        icon !== 'lock' &&
        icon !== 'chevron-right' &&
        icon !== 'plus-circle' ? (
          <FontAwesome6
            name={icon}
            iconStyle="solid"
            style={{ marginRight: s(8) }}
            size={iconSize(iconSizeProp ?? 20)}
            color={textColor || 'white'}
          />
        ) : null}
        <Text
          style={[
            styles.text,
            fontFamily
              ? { fontFamily: fontFamily }
              : { fontFamily: GTFonts.semibold },
            {
              fontSize: fontSize ? fs(fontSize) : fs(20),
              lineHeight: fontSize ? fs(fontSize + 2) : fs(22),
            },
            icon === 'lock' && { paddingLeft: s(5) },
            { color: textColor || 'white' },
          ]}>
          {text}
        </Text>
        {icon && icon === 'lock' ? (
          <Entypo
            name={icon}
            style={{ paddingLeft: s(8) }}
            size={iconSize(iconSizeProp ?? 20)}
            color={textColor || 'white'}
          />
        ) : null}
        {(icon === 'chevron-right' || trailingIcon === 'chevron-right') &&
        !loading ? (
          <MaterialCommunityIcons
            name="chevron-right"
            style={{ marginLeft: s(4) }}
            size={iconSize(iconSizeProp ?? 20)}
            color={textColor || 'white'}
          />
        ) : null}
        {/* Same arrow as PickerFooterButton's next-step buttons. */}
        {trailingIcon === 'arrow-right' && !loading ? (
          <MaterialCommunityIcons
            name="arrow-right"
            style={{ marginLeft: s(8) }}
            size={iconSize(iconSizeProp ?? 20)}
            color={textColor || 'white'}
          />
        ) : null}
        {loading ? (
          <ActivityIndicator
            style={{ paddingLeft: s(8) }}
            size="small"
            color={textColor || 'white'}
          />
        ) : null}
      </TouchableOpacity>
    </View>
  );
};
export default Button;

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
  },
  fullWidth: {
    alignSelf: 'stretch',
  },
  button: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  text: {
    fontSize: fs(20),
    // lineHeight: fs(20),
    color: 'white',
  },
});
